/**
 * Claves (keys) de Supabase Storage derivadas del nombre de un archivo.
 *
 * POR QUÉ EXISTE ESTE ARCHIVO
 * ──────────────────────────────────────────────────────────────────────────────
 * `storage-api` valida la key contra una whitelist (`isValidKey`) y responde
 * HTTP 400 `InvalidKey` si no casa. La whitelist es, literalmente:
 *
 *     \w  /  !  -  .  *  '  (  )  espacio  &  $  @  =  ;  :  +  ,  ?
 *
 * y `\w` en JavaScript es solo `[A-Za-z0-9_]` — no incluye acentos ni ñ.
 *
 * Verificado contra Preview (`supabase-dev.sozu.com`, storage-api 1.48.26) el
 * 2026-09-15, subiendo cada nombre al bucket `documentos`:
 *
 *     cotizacion.pdf                200
 *     Proyecto de Escritura.pdf     200   ← espacios y mayúsculas sí pasan
 *     archivo_con_espacios.pdf      200
 *     Cotización 510.pdf            400 InvalidKey
 *     Cotización 510 (1).pdf        400 InvalidKey
 *     Añejo Ñandú.pdf               400 InvalidKey
 *     100%_pagado.pdf               400 InvalidKey
 *     c[1].pdf                      400 InvalidKey
 *     reporte{final}.pdf            400 InvalidKey
 *     guion–largo.pdf               400 InvalidKey  (guion largo, no ASCII)
 *
 * O sea: el defecto no es "el acento". Es armar la key con el nombre original.
 *
 * REGLA
 * ──────────────────────────────────────────────────────────────────────────────
 * La key NUNCA se arma con el nombre que trae el archivo. El nombre original es
 * un dato de presentación y vive en `documentos.descripcion` (visible y
 * editable); la key es un identificador físico derivado: sanitizado y con
 * timestamp, para que cada carga tenga su propio objeto y no pise la anterior.
 *
 * Dos trampas más que esto evita, aparte del 400:
 *   - `? & +` sí pasan `isValidKey`, pero `getPublicUrl` arma la URL con
 *     `encodeURI`, que no los escapa: la URL guardada en BD queda rota.
 *   - Con el nombre original + `upsert: true`, dos cargas del mismo archivo
 *     comparten key: la segunda sobrescribe la primera y las filas viejas de
 *     `documentos` terminan apuntando al archivo nuevo.
 *
 * El formato `{tipoId}-{timestamp}-{nombre}` es el que ya usaba
 * `useExpedienteDesarrollo` (expediente desarrollo del Portal Notaría); aquí se
 * centraliza para que todas las cargas del portal compartan la misma regla.
 */

/** Whitelist real de `storage-api`. Se usa como red de seguridad, no como filtro. */
const KEY_VALIDA = /^[\w/!\-.*'() &$@=;:+,?]+$/;

/** Tope del nombre visible que se guarda en `documentos.descripcion`. */
export const MAX_NOMBRE_ARCHIVO = 120;

/** Tope de la parte derivada del nombre dentro de la key. */
const MAX_BASE_KEY = 80;

/** `true` si Storage aceptaría esta key. Espejo de `isValidKey` de storage-api. */
export function isValidStorageKey(key: string): boolean {
  return key.length > 0 && KEY_VALIDA.test(key);
}

function limpiarSegmento(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')  // quita los diacríticos ya separados
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._-]+|[._-]+$/g, '');
}

/**
 * Nombre de archivo seguro para una key: sin acentos, sin espacios, solo
 * `[A-Za-z0-9._-]`. Conserva la extensión y acota el largo.
 *
 * `Cotización 510 (1).pdf` → `Cotizacion_510_1.pdf`
 */
export function sanitizeFilename(nombre: string): string {
  const corte = nombre.lastIndexOf('.');
  const tieneExt = corte > 0 && corte < nombre.length - 1;
  const base = limpiarSegmento(tieneExt ? nombre.slice(0, corte) : nombre).slice(0, MAX_BASE_KEY);
  const ext = tieneExt ? limpiarSegmento(nombre.slice(corte + 1)).toLowerCase().slice(0, 10) : '';
  const baseFinal = base.replace(/[._-]+$/g, '') || 'archivo';
  return ext ? `${baseFinal}.${ext}` : baseFinal;
}

/**
 * Extensión segura de un archivo, para las keys que solo conservan la extensión
 * (`{entidad}_{timestamp}.{ext}`) y no el nombre.
 *
 * `nombre.split('.').pop()` no sirve: con un archivo SIN punto devuelve el nombre
 * completo — `Cotización` se vuelve la "extensión" y la key termina con acento y
 * un 400. Aquí un archivo sin extensión real cae al respaldo.
 *
 * `Cotización 510.pdf` → `pdf` · `Cotización` → respaldo · `foto.JPG` → `jpg`
 */
export function safeExtension(nombre: string, respaldo = 'bin'): string {
  const corte = nombre.lastIndexOf('.');
  if (corte <= 0 || corte === nombre.length - 1) return respaldo;
  const ext = limpiarSegmento(nombre.slice(corte + 1))
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 10);
  return ext || respaldo;
}

/**
 * Key completa dentro del bucket: `{prefijo}/{scopeId}/{tipoId}-{ts}-{nombre}`.
 *
 * Determinística respecto de sus entradas y única por carga (el timestamp evita
 * que una versión nueva pise a la anterior: las dos quedan en Storage y el
 * documento vigente lo decide la FK en BD, no el archivo).
 *
 * @param prefijo         carpeta lógica dentro del bucket (`cotizaciones_notaria`).
 * @param scopeId         a qué pertenece: id de cuenta de cobranza, proyecto, etc.
 * @param tipoId          `tipos_documento.id`.
 * @param nombreOriginal  el nombre con el que llegó el archivo.
 * @param ahora           inyectable para pruebas.
 */
export function buildDocumentoKey({
  prefijo,
  scopeId,
  tipoId,
  nombreOriginal,
  ahora = Date.now(),
}: {
  prefijo: string;
  scopeId: number | string;
  tipoId: number | string;
  nombreOriginal: string;
  ahora?: number;
}): string {
  const key = `${prefijo}/${scopeId}/${tipoId}-${ahora}-${sanitizeFilename(nombreOriginal)}`;
  if (!isValidStorageKey(key)) {
    // Solo se puede llegar aquí si `prefijo` o `scopeId` traen basura: el nombre
    // del archivo ya salió sanitizado. Mejor fallar aquí que con un 400 opaco.
    throw new Error(`Ruta de almacenamiento inválida: ${key}`);
  }
  return key;
}

/**
 * Nombre legible a partir de la URL de Storage, para documentos que se cargaron
 * antes de que se guardara `descripcion`. Quita el prefijo `{tipoId}-{ts}-`.
 */
export function nombreDesdeUrl(url: string): string {
  try {
    const base = decodeURIComponent(url.split('?')[0].split('/').pop() ?? '');
    const sinPrefijo = base.replace(/^\d+-\d{10,}-/, '');
    return sinPrefijo || 'Documento';
  } catch {
    return 'Documento';
  }
}

/** Lo que se le muestra al usuario: el nombre guardado o, si no hay, el de la URL. */
export function nombreVisibleDocumento(descripcion: string | null | undefined, url: string | null | undefined): string {
  const limpio = descripcion?.trim();
  if (limpio) return limpio;
  return url ? nombreDesdeUrl(url) : 'Documento';
}
